import { MouseEvent, useCallback, useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { ClipboardText } from "@phosphor-icons/react"
import { Button } from "@/components/ui/button"
import { TextArea } from "@/components/ui/input"
import { ScrollArea } from "@/components/ui/scroll-area"
import { toast } from "@/hooks/useToast"

const OFFSET_LEFT_DEFAULT = 105

type Props = {
  offsetLeft?: number
  content: string
}

const BROWSER_HEIGHT = innerHeight
const BROWSER_MIN_HEIGHT = 16
const NOTE_MIN_HEIGHT = 120

export const ShareNote = ({ offsetLeft = OFFSET_LEFT_DEFAULT, content }: Props) => {
  const { t } = useTranslation()
  const [position, setPosition] = useState({
    x: offsetLeft,
    y: BROWSER_HEIGHT - NOTE_MIN_HEIGHT - BROWSER_MIN_HEIGHT,
  })

  const [offset, setOffset] = useState({ x: 0, y: 0 })
  const [isDragging, setIsDragging] = useState(false)

  const handleMouseDown = useCallback(
    (e: MouseEvent<HTMLDivElement, globalThis.MouseEvent>) => {
      document.body.classList.add("select-none")
      setIsDragging(true)
      setOffset({
        x: e.clientX - position.x,
        y: e.clientY - position.y,
      })
    },
    [position],
  )

  const handleMouseMove = useCallback(
    (e: globalThis.MouseEvent) => {
      if (isDragging) {
        setPosition({
          x: e.clientX - offset.x,
          y: e.clientY - offset.y,
        })
      }
    },
    [isDragging, offset],
  )

  const handleMouseUp = useCallback(() => {
    setIsDragging(false)
    document.body.classList.remove("select-none")
  }, [])

  useEffect(() => {
    if (isDragging) {
      window.addEventListener("mousemove", handleMouseMove)
      window.addEventListener("mouseup", handleMouseUp)
    }
    return () => {
      window.removeEventListener("mousemove", handleMouseMove)
      window.removeEventListener("mouseup", handleMouseUp)
    }
  }, [isDragging, handleMouseMove, handleMouseUp])

  return (
    <div
      className="bg-warning-surface-subdued shadow-5 share-note pointer-events-auto fixed bottom-4 z-100 flex max-h-100 min-h-10 w-93.75 cursor-move flex-col overflow-hidden rounded-xl"
      style={{
        left: position.x,
        top: position.y,
      }}
      onMouseDown={(e) => handleMouseDown(e)}>
      <div className="mx-4 mt-2 mr-2 -mb-4 flex items-center justify-between">
        <span className="text-on-warning-surface text-xs leading-4 tracking-[0.25px]">
          {t("patient.temporaryNotes.notes")}
        </span>
        <div className="flex gap-2">
          <Button
            onClick={() => {
              navigator.clipboard.writeText(content)
              toast({
                description: t("patient.temporaryNotes.copied"),
              })
            }}
            variant="circle"
            className="h-8 w-8 p-0"
            title={t("common.button.copy")}>
            <ClipboardText className="text-on-surface-subdued" />
          </Button>
        </div>
      </div>
      <ScrollArea className="mt-4 rounded-xl" viewportClassName="max-h-90">
        <TextArea
          data-testId="shared-note-value"
          wrapperProps={{ className: "flex flex-col" }}
          onMouseDown={(e) => {
            e.stopPropagation()
          }}
          value={content}
          readOnly
          className="scrollbar scrollbar-thumb-rounded-full hover:scrollbar-thumb-surface-pressed h-90! min-h-17 resize-none overflow-y-auto rounded-xl border-0 border-transparent bg-transparent p-4 pt-0 leading-5 hover:bg-transparent focus:border-0 focus:border-transparent focus:ring-0 focus:outline-hidden! active:border-0"
        />
      </ScrollArea>
    </div>
  )
}
